import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Alert,
  IconButton,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Avatar,
  Skeleton,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { clientiService, cantieriService, interventiService } from '../services/api';
import dayjs from 'dayjs';
import { iniziali } from '../utils/formato';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cliente-tabpanel-${index}`}
      aria-labelledby={`cliente-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Componente StatBox per le statistiche
const StatBox = ({ icon: Icon, value, label, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      bgcolor: 'rgba(255,255,255,0.1)',
      borderRadius: 2,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        bgcolor: color || 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ color: 'white', fontSize: 20 }} />
    </Box>
    <Box>
      <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

// Componente per sezione form
const FormSection = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        mb: 2,
        fontSize: '0.75rem',
      }}
    >
      {title}
    </Typography>
    <Grid container spacing={2.5}>
      {children}
    </Grid>
  </Box>
);

const ClienteDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [cliente, setCliente] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [interventi, setInterventi] = useState([]);

  const [formData, setFormData] = useState({
    ragione_sociale: '',
    partita_iva: '',
    codice_fiscale: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    telefono: '',
    email: '',
    pec: '',
    referente: '',
    telefono_referente: '',
    email_referente: '',
    note: '',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clienteRes, cantieriRes, interventiRes] = await Promise.all([
        clientiService.getById(id),
        cantieriService.getAll(),
        interventiService.getAll(),
      ]);

      setCliente(clienteRes.data);
      setFormData(clienteRes.data);

      const clienteCantieri = cantieriRes.data.filter(c => c.cliente_id === parseInt(id));
      setCantieri(clienteCantieri);

      const clienteInterventi = interventiRes.data.filter(i => i.cliente_id === parseInt(id));
      setInterventi(clienteInterventi);

    } catch (error) {
      setError('Errore nel caricamento dei dati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    try {
      setError('');
      await clientiService.update(id, formData);
      setSuccess('Cliente aggiornato con successo');
      setEditing(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };


  const getStatusColor = (stato) => {
    switch (stato) {
      case 'attivo':
        return { bg: '#dcfce7', color: '#166534' };
      case 'completato':
        return { bg: '#dcfce7', color: '#166534' };
      case 'in_corso':
        return { bg: '#fef3c7', color: '#92400e' };
      case 'programmato':
        return { bg: '#dbeafe', color: '#1e40af' };
      case 'sospeso':
        return { bg: '#fef2f2', color: '#991b1b' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Box sx={{ bgcolor: '#1a1a1a', pt: 4, pb: 10, px: 3 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={48} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
              <Box>
                <Skeleton variant="text" width={200} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                <Skeleton variant="text" width={150} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Box>
            </Box>
          </Container>
        </Box>
        <Container maxWidth="lg" sx={{ mt: -6 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!cliente) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Cliente non trovato
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/clienti')}
            sx={{
              mt: 2,
              bgcolor: '#e50000',
              '&:hover': { bgcolor: '#c50000' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Torna ai Clienti
          </Button>
        </Paper>
      </Box>
    );
  }

  const cantieriAttivi = cantieri.filter(c => c.stato === 'attivo').length;
  const interventiCompletati = interventi.filter(i => i.stato === 'completato').length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Header scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          pt: { xs: 3, sm: 4 },
          pb: { xs: 14, sm: 16 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(229,0,0,0.3) 0%, transparent 70%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Breadcrumb / Back */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/clienti')}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              mb: 3,
              textTransform: 'none',
              '&:hover': {
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Torna ai Clienti
          </Button>

          {/* Info cliente */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: '#e50000',
                fontSize: '1.5rem',
                fontWeight: 800,
              }}
            >
              {iniziali(cliente.ragione_sociale)}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  mb: 1,
                }}
              >
                {cliente.ragione_sociale}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                {cliente.partita_iva && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontFamily: 'monospace',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    P.IVA: {cliente.partita_iva}
                  </Typography>
                )}
                {cliente.citta && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {cliente.citta}{cliente.provincia ? ` (${cliente.provincia})` : ''}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Pulsante modifica (desktop) */}
            {!isMobile && tabValue === 0 && (
              <Button
                variant={editing ? 'outlined' : 'contained'}
                startIcon={editing ? <CloseIcon /> : <EditIcon />}
                onClick={() => editing ? setEditing(false) : setEditing(true)}
                sx={{
                  bgcolor: editing ? 'transparent' : '#e50000',
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: editing ? 'rgba(255,255,255,0.1)' : '#c50000',
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                }}
              >
                {editing ? 'Annulla' : 'Modifica'}
              </Button>
            )}
          </Box>

          {/* Statistiche */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StatBox
              icon={BuildIcon}
              value={cantieri.length}
              label="Cantieri"
              color="rgba(59,130,246,0.3)"
            />
            <StatBox
              icon={CheckCircleIcon}
              value={cantieriAttivi}
              label="Attivi"
              color="rgba(16,185,129,0.3)"
            />
            <StatBox
              icon={AssignmentIcon}
              value={interventi.length}
              label="Interventi"
              color="rgba(245,158,11,0.3)"
            />
            <StatBox
              icon={ScheduleIcon}
              value={interventiCompletati}
              label="Completati"
              color="rgba(139,92,246,0.3)"
            />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="lg" sx={{ mt: { xs: -10, sm: -12 }, pb: 4, position: 'relative', zIndex: 2 }}>
        {/* Alerts */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
              sx={{
                px: { xs: 0, sm: 2 },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: 56,
                  color: '#6b7280',
                  '&.Mui-selected': {
                    color: '#e50000',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#e50000',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab
                icon={<BusinessIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Anagrafica"
              />
              <Tab
                icon={<BuildIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`Cantieri (${cantieri.length})`}
              />
              <Tab
                icon={<AssignmentIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`Interventi (${interventi.length})`}
              />
            </Tabs>
          </Box>

          {/* Tab Anagrafica */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
              <FormSection title="Dati Aziendali">
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="ragione_sociale"
                    label="Ragione Sociale"
                    fullWidth
                    required
                    value={formData.ragione_sociale}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="partita_iva"
                    label="Partita IVA"
                    fullWidth
                    value={formData.partita_iva}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="codice_fiscale"
                    label="Codice Fiscale"
                    fullWidth
                    value={formData.codice_fiscale}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="telefono"
                    label="Telefono"
                    fullWidth
                    value={formData.telefono}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              <FormSection title="Indirizzo">
                <Grid item xs={12}>
                  <TextField
                    name="indirizzo"
                    label="Indirizzo"
                    fullWidth
                    required
                    value={formData.indirizzo}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    name="citta"
                    label="Città"
                    fullWidth
                    required
                    value={formData.citta}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    name="cap"
                    label="CAP"
                    fullWidth
                    required
                    value={formData.cap}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    name="provincia"
                    label="Provincia"
                    fullWidth
                    required
                    value={formData.provincia}
                    onChange={handleInputChange}
                    disabled={!editing}
                    inputProps={{ maxLength: 2 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              <FormSection title="Contatti">
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="email"
                    label="Email"
                    fullWidth
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="pec"
                    label="PEC"
                    fullWidth
                    type="email"
                    value={formData.pec}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              <FormSection title="Referente">
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="referente"
                    label="Nome Referente"
                    fullWidth
                    value={formData.referente}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="telefono_referente"
                    label="Telefono Referente"
                    fullWidth
                    value={formData.telefono_referente}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="email_referente"
                    label="Email Referente"
                    fullWidth
                    type="email"
                    value={formData.email_referente}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              <FormSection title="Note">
                <Grid item xs={12}>
                  <TextField
                    name="note"
                    label="Note"
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.note}
                    onChange={handleInputChange}
                    disabled={!editing}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </FormSection>

              {/* Pulsanti salvataggio */}
              {editing && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    onClick={() => {
                      setEditing(false);
                      setFormData(cliente);
                    }}
                    sx={{
                      color: '#6b7280',
                      px: 3,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Annulla
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{
                      bgcolor: '#e50000',
                      px: 3,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#c50000' },
                    }}
                  >
                    Salva Modifiche
                  </Button>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Tab Cantieri */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {cantieri.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <BuildIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    Nessun cantiere
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Questo cliente non ha ancora cantieri associati
                  </Typography>
                </Box>
              ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {cantieri.map((cantiere) => {
                    const statusColor = getStatusColor(cantiere.stato);
                    return (
                      <Paper
                        key={cantiere.id}
                        elevation={0}
                        onClick={() => navigate(`/cantieri/${cantiere.id}`)}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#e50000',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {cantiere.codice}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {cantiere.nome}
                            </Typography>
                          </Box>
                          <Chip
                            label={cantiere.stato}
                            size="small"
                            sx={{
                              bgcolor: statusColor.bg,
                              color: statusColor.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              textTransform: 'capitalize',
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {cantiere.indirizzo}, {cantiere.citta}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Codice</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Nome</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Indirizzo</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Città</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Stato</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cantieri.map((cantiere) => {
                        const statusColor = getStatusColor(cantiere.stato);
                        return (
                          <TableRow
                            key={cantiere.id}
                            sx={{
                              '&:hover': { bgcolor: '#fafafa' },
                              transition: 'background-color 0.2s',
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                                {cantiere.codice}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {cantiere.nome}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{cantiere.indirizzo}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{cantiere.citta}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={cantiere.stato}
                                size="small"
                                sx={{
                                  bgcolor: statusColor.bg,
                                  color: statusColor.color,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  textTransform: 'capitalize',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Visualizza dettagli">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/cantieri/${cantiere.id}`)}
                                  sx={{
                                    color: '#3b82f6',
                                    '&:hover': { bgcolor: '#eff6ff' },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>

          {/* Tab Interventi */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {interventi.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    Nessun intervento
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Questo cliente non ha ancora interventi associati
                  </Typography>
                </Box>
              ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {interventi.map((intervento) => {
                    const statusColor = getStatusColor(intervento.stato);
                    return (
                      <Paper
                        key={intervento.id}
                        elevation={0}
                        onClick={() => navigate(`/interventi/${intervento.id}`)}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#e50000',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <CalendarIcon sx={{ fontSize: 14, color: '#e50000' }} />
                              <Typography variant="body2" fontWeight={700} color="#e50000">
                                {dayjs(intervento.data).format('DD MMM YYYY')}
                              </Typography>
                            </Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {intervento.cantiere}
                            </Typography>
                          </Box>
                          <Chip
                            label={intervento.stato}
                            size="small"
                            sx={{
                              bgcolor: statusColor.bg,
                              color: statusColor.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              textTransform: 'capitalize',
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Orario:</strong> {intervento.ora_inizio} - {intervento.ora_fine}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Tipo:</strong> {intervento.tipologia}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Data</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Cantiere</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Orario</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Tipologia</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Stato</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interventi.map((intervento) => {
                        const statusColor = getStatusColor(intervento.stato);
                        return (
                          <TableRow
                            key={intervento.id}
                            sx={{
                              '&:hover': { bgcolor: '#fafafa' },
                              transition: 'background-color 0.2s',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 16, color: '#e50000' }} />
                                <Typography variant="body2" fontWeight={600}>
                                  {dayjs(intervento.data).format('DD/MM/YYYY')}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{intervento.cantiere}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {intervento.ora_inizio} - {intervento.ora_fine}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{intervento.tipologia}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={intervento.stato}
                                size="small"
                                sx={{
                                  bgcolor: statusColor.bg,
                                  color: statusColor.color,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  textTransform: 'capitalize',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Visualizza dettagli">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/interventi/${intervento.id}`)}
                                  sx={{
                                    color: '#3b82f6',
                                    '&:hover': { bgcolor: '#eff6ff' },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>
        </Paper>
      </Container>

      {/* FAB Mobile per modifica */}
      {isMobile && tabValue === 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
        >
          {editing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{
                  bgcolor: '#e50000',
                  borderRadius: 28,
                  px: 3,
                  py: 1.5,
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#c50000' },
                }}
              >
                Salva
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditing(false);
                  setFormData(cliente);
                }}
                sx={{
                  borderRadius: 28,
                  px: 3,
                  py: 1.5,
                  bgcolor: 'white',
                  borderColor: '#e5e7eb',
                  color: '#6b7280',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                }}
              >
                Annulla
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
              sx={{
                bgcolor: '#e50000',
                borderRadius: 28,
                px: 3,
                py: 1.5,
                boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#c50000' },
              }}
            >
              Modifica
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ClienteDettaglio;
